import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import DatePicker from "react-datepicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

const isInSameWeek = (dayA, dayB) => {
  if (!dayB) return false;
  return dayA.isSame(dayB, "week");
};


const ServerDay = (props) => {
  const { day, selectedWeek, ...other } = props;

  return (
    <PickersDay
      {...other}
      day={day}
      selected={false}
      sx={{
        borderRadius: 0,
        backgroundColor: isInSameWeek(day, selectedWeek)
          ? "#F96343"
          : "transparent",
        color: isInSameWeek(day, selectedWeek) ? "#fff" : "#667085",
        "&:hover": {
          backgroundColor: isInSameWeek(day, selectedWeek)
            ? "#F96343"
            : "#FCEBE8",
        },
        ...(day.day() === 0 &&
          isInSameWeek(day, selectedWeek) && {
            borderTopLeftRadius: 20,
            borderBottomLeftRadius: 20,
          }),
        ...(day.day() === 6 &&
          isInSameWeek(day, selectedWeek) && {
            borderTopRightRadius: 20,
            borderBottomRightRadius: 20,
          }),
      }}
    />
  );
};


const calendarTheme = createTheme({
  palette: {
    primary: {
      main: "#F96343",
    },
  },
});


// Simple dropdown component matching your existing styles
const DateFilterDropdown = ({ value, options, onChange, placeholder = "Select", isDropdownOpen, setIsDropdownOpen, setHoveredOption, containerRef }) => {
  const dropdownRef = useRef(null);

  const selectedOption = options.find(opt => opt.value === value);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && (!containerRef.current || !containerRef.current.contains(event.target))) {
        setIsDropdownOpen(false);
        setHoveredOption(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div 
      ref={dropdownRef} 
      className="date-filter-dropdown-wrapper"
    >
      <div
        className={`date-filter-dropdown-control ${isDropdownOpen ? "date-filter-dropdown-open" : ""}`}
        onClick={() => {setIsDropdownOpen(!isDropdownOpen); setHoveredOption(null); }}
      >
        <span className={selectedOption ? "date-filter-dropdown-value" : "date-filter-dropdown-placeholder"}>
          {selectedOption?.label || placeholder}
        </span>
        <span className="date-filter-dropdown-icon">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            style={{
              transform: isDropdownOpen ? "rotate(180deg)" : "none",
              transition: "transform 0.2s"
            }}
          >
            <path
              d="M5 7.5L10 12.5L15 7.5"
              stroke="#667085"
              strokeWidth="1.66667"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </div>

      {isDropdownOpen && (
        <div className="date-filter-dropdown-menu">
          <div className="date-filter-dropdown-options">
            {options.map((option) => (
              <div
                key={option.value}
                className={`date-filter-dropdown-option ${value === option.value ? "date-filter-dropdown-option-selected" : ""}`}
                onClick={() => {
                  onChange(option.value);
                  console.log("valueeeeeeeeee", option.value)
                  // setIsOpen(false);
                }}
                onMouseEnter={option.onMouseEnter}
                onMouseLeave={option.onMouseLeave}
              >
                {option.label}
                {value === option.value && !['select_month', 'select_week'].includes(option.value) &&(
                  <svg
                    className="date-filter-dropdown-option-selected-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M16.6668 5L7.50016 14.1667L3.3335 10"
                      stroke="#F55533"
                      strokeWidth="1.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                {value === option.value && ['select_month', 'select_week'].includes(option.value)&&(
                  <svg className="date-filter-dropdown-option-selected-icon" xmlns="http://www.w3.org/2000/svg" width="7" height="12" viewBox="0 0 7 12" fill="none">
                    <path d="M0.833008 10.8334L5.83301 5.83337L0.833008 0.833374" stroke="#F55533" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                )}

              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Main DateFilter component
const DateRangeFilter = React.memo(({ onFilterChange, activeFilterType }) => {
  const [hoveredOption, setHoveredOption] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedWeek, setSelectedWeek] = useState(dayjs());

  const containerRef = useRef(null)

  // useEffect(() => {
  //   const handleClickOutside = (event) => {
  //     if (containerRef.current && !containerRef.current.contains(event.target)) {
  //       setIsDropdownOpen(false);
  //       setHoveredOption(null);
  //     }
  //   };
  //   document.addEventListener("mousedown", handleClickOutside);
  //   return () => document.removeEventListener("mousedown", handleClickOutside);
  // }, []);


  const filterOptions = useMemo(() => [
    { 
      label: "All", 
      value: "all",
      onMouseEnter: () => setHoveredOption(null),
      onMouseLeave: () => {}
    },
    { 
      label: "This Week", 
      value: "this_week",
      onMouseEnter: () => setHoveredOption(null),
      onMouseLeave: () => {}
    },
    { 
      label: "This Month", 
      value: "this_month",
      onMouseEnter: () => setHoveredOption(null),
      onMouseLeave: () => {}
    },
    { 
      label: "Select Week", 
      value: "select_week",
      onMouseEnter: () => setHoveredOption("select_week"),
      onMouseLeave: () => setHoveredOption("select_week")
    },
    { 
      label: "Select Month", 
      value: "select_month",
      onMouseEnter: () => setHoveredOption("select_month"),
      onMouseLeave: () => setHoveredOption("select_month")
    }
  ], []);

  const getDateRange = useCallback((type, date = new Date()) => {
    switch (type) {
      case "this_week":
        return {
          start: dayjs().startOf('week').toDate(),
          end: dayjs().endOf('week').toDate()
        };
      case "this_month":
        return {
          start: dayjs().startOf('month').toDate(),
          end: dayjs().endOf('month').toDate()
        };
      case "select_week":
        return {
          start: dayjs(date).startOf('week').toDate(),
          end: dayjs(date).endOf('week').toDate()
        };
      case "select_month":
        return {
          start: dayjs(date).startOf('month').toDate(),
          end: dayjs(date).endOf('month').toDate()
        };
      default:
        return { start: null, end: null };
    }
  }, []);

  // const getFilterLabel = useCallback((type, date = new Date()) => {
  //   switch (type) {
  //     case "all": return "All";
  //     case "this_week": return "This Week";
  //     case "this_month": return "This Month";
  //     case "select_week": return `Week of ${dayjs(date).startOf('week').format('MMM D, YYYY')}`;
  //     case "select_month": return dayjs(date).format('MMMM YYYY');
  //     default: return "All";
  //   }
  // }, []);

  // FIXED: This function updates both the selected filter and the displayed label
  const handleFilterSelect = useCallback((value) => {
    // Don't close picker for select options
    if (value === "select_week" || value === "select_month") {
      setHoveredOption(value);
      return;
    }

    // Apply filter immediately for non-picker options
    const { start, end } = getDateRange(value);
    setHoveredOption(null);
    setIsDropdownOpen(false);
    onFilterChange({
      type: value,
      start: moment(start).format("YYYY-MM-DD"),
      end: moment(end).format("YYYY-MM-DD"),
      // filterLabel: getFilterLabel(value)
    });
  }, [getDateRange, onFilterChange]);

  const handleDateSelect = useCallback((date) => {
    const dateObj = date instanceof dayjs ? date.toDate() : date;
    const type = hoveredOption; // select_week or select_month
    const { start, end } = getDateRange(type, dateObj);
    setSelectedDate(dateObj);
    if(type === "select_week") setSelectedWeek(dayjs(dateObj));
    onFilterChange({ type, start: moment(start).format("YYYY-MM-DD"), end: moment(end).format("YYYY-MM-DD") });
    setHoveredOption(null);
    setIsDropdownOpen(false);
  }, [hoveredOption, getDateRange, onFilterChange]);

  return (
    <div className="date-filter-wrapper">
      <DateFilterDropdown
        value={activeFilterType}
        options={filterOptions}
        onChange={handleFilterSelect}
        placeholder="Select date range"
        isDropdownOpen={isDropdownOpen}
        setIsDropdownOpen={setIsDropdownOpen}
        setHoveredOption={setHoveredOption}
        containerRef={containerRef}
      />

      {/* Week Picker */}
      {hoveredOption === "select_week" && (
        <div className="weekPickerMainContainer" ref={containerRef}>
          <ThemeProvider theme={calendarTheme}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar
                value={selectedWeek}
                onChange={(newValue, selectionState) => {
                  console.log("newValue", selectionState);
                  if(selectionState === "finish"){
                    setSelectedWeek(newValue);

                    const { start, end } = getDateRange("select_week", newValue.toDate());

                    onFilterChange({
                      type: "select_week",
                      start: moment(start).format("YYYY-MM-DD"),
                      end: moment(end).format("YYYY-MM-DD"),
                      // filterLabel: getFilterLabel(
                      //   "select_week",
                      //   newValue.toDate()
                      // ),
                    });
                    setHoveredOption(null);
                    setIsDropdownOpen(false);
                  }
                }}
                slots={{ day: ServerDay }}
                slotProps={{
                  day: {
                    selectedWeek,
                  },
                }}
              />
            </LocalizationProvider>
          </ThemeProvider>
        </div>
      )}



      {/* Month Picker */}
      {hoveredOption === "select_month" && (
        <div className="monthPickerMainContainer" ref={containerRef}> 
            <DatePicker
            selected={selectedDate}
            onChange={handleDateSelect}
            showMonthYearPicker
            dateFormat="MMMM yyyy"
            inline
        />
        </div>
      )}
    </div>
  );
});

export default DateRangeFilter;